import L from 'leaflet';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ubicacionForm from '../assets/images/ubicacionForm.png';
import { Link, useNavigate } from 'react-router-dom';
import { Departamento, Municipio, Distrito, getSession, PuntoCentral, formatHijosData } from '../utils/utils';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";

const customIcon = new L.Icon({
	iconUrl: ubicacionForm, // Ruta de la imagen
	iconSize: [41, 41], // Tamaño del ícono
	iconAnchor: [12, 41], // Punto de anclaje del ícono
	popupAnchor: [1, -34], // Punto de anclaje del popup
	shadowSize: [41, 41], // Tamaño de la sombra
});

function FormularioHijos() {
	const navigate = useNavigate();
	const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm();

	let totalErrors = Object.keys(errors).length;

	// Estados principales
	const [departamento, setDepartamento] = useState([]);
	const [hijosTotal, setHijosTotal] = useState(0);
	const [hijosAplican, setHijosAplican] = useState(0);
	const [isOpen, setIsOpen] = useState(false);
	const [userInfo, setUserInfo] = useState({ role: null });

	// Estados para manejar datos geográficos por hijo
	const [geoData, setGeoData] = useState({
		municipios: {},
		distritos: {},
		municipios2: {},
		distritos2: {},
		centros: {},
		centros2: {},
		positions: {},
		positions2: {}
	});

	// Cargar datos iniciales
	useEffect(() => {
		const loadInitialData = async () => {
			const session = await getSession();
			setUserInfo(session);

			const deptos = await Departamento();
			setDepartamento(deptos);

			const savedValues = JSON.parse(localStorage.getItem('FormHijos')) || {
				hijos_cant_conyugue: 0,
				hijos_apli_conyugue: 0
			};

			if (savedValues.hijos_cant_conyugue) {
				setHijosTotal(savedValues.hijos_cant_conyugue);
			}

			if (savedValues.hijos_apli_conyugue) {
				setHijosAplican(savedValues.hijos_apli_conyugue);

				// Cargar datos geográficos para cada hijo que aplica
				const newGeoData = {
					municipios: {},
					distritos: {},
					municipios2: {},
					distritos2: {},
					centros: {},
					centros2: {},
					positions: {},
					positions2: {}
				};

				for (let i = 1; i <= savedValues.hijos_apli_conyugue; i++) {
					// Datos de residencia
					if (savedValues[`departamento_residencia_${i}`]) {
						const [munis, centro] = await Promise.all([
							Municipio(savedValues[`departamento_residencia_${i}`]),
							PuntoCentral(savedValues[`departamento_residencia_${i}`])
						]);

						newGeoData.municipios[i] = munis || [];
						newGeoData.centros[i] = centro || { latitude: "13.79459900", longitude: "-88.93000000" };

						if (savedValues[`municipio_residencia_${i}`]) {
							const dists = await Distrito(savedValues[`municipio_residencia_${i}`]);
							newGeoData.distritos[i] = dists || [];
						}
					}

					// Datos de cuidado
					if (savedValues[`departamento_cuido_${i}`]) {
						const [munis2, centro2] = await Promise.all([
							Municipio(savedValues[`departamento_cuido_${i}`]),
							PuntoCentral(savedValues[`departamento_cuido_${i}`])
						]);

						newGeoData.municipios2[i] = munis2 || [];
						newGeoData.centros2[i] = centro2 || { latitude: "13.79459900", longitude: "-88.93000000" };

						if (savedValues[`municipio_cuido_${i}`]) {
							const dists2 = await Distrito(savedValues[`municipio_cuido_${i}`]);
							newGeoData.distritos2[i] = dists2 || [];
						}
					}

					// Posiciones del mapa
					if (savedValues[`latitud_residencia_${i}`] && savedValues[`longitud_residencia_${i}`]) {
						newGeoData.positions[i] = {
							lat: parseFloat(savedValues[`latitud_residencia_${i}`]),
							lng: parseFloat(savedValues[`longitud_residencia_${i}`])
						};
					}

					if (savedValues[`latitud_cuido_${i}`] && savedValues[`longitud_cuido_${i}`]) {
						newGeoData.positions2[i] = {
							lat: parseFloat(savedValues[`latitud_cuido_${i}`]),
							lng: parseFloat(savedValues[`longitud_cuido_${i}`])
						};
					}
				}

				setGeoData(newGeoData);
			}

			reset(savedValues);
		};

		loadInitialData();
	}, []);

	// Guardar valores en localStorage cuando cambian
	useEffect(() => {
		const subscription = watch((values) => {
			localStorage.setItem('FormHijos', JSON.stringify(values));
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	// Manejar cambios en cantidad de hijos
	const handleHijosTotalChange = (e) => {
		const cantidad = parseInt(e.target.value) || 0;
		setHijosTotal(cantidad);
		setValue('hijos_cant_conyugue', cantidad);

		// Si reducimos la cantidad, ajustamos hijos que aplican
		if (cantidad < hijosAplican) {
			setHijosAplican(cantidad);
			setValue('hijos_apli_conyugue', cantidad);
		}
	};

	// Manejar cambios en hijos que aplican
	const handleHijosAplicanChange = (e) => {
		const cantidad = parseInt(e.target.value) || 0;
		setHijosAplican(cantidad);
		setValue('hijos_apli_conyugue', cantidad);
	};

	// Manejar cambios en departamento (residencia)
	const handleDepartamentoChange = async (e, hijoNum) => {
		const deptoId = e.target.value;
		setValue(`departamento_residencia_${hijoNum}`, deptoId);
		setValue(`municipio_residencia_${hijoNum}`, "");
		setValue(`distrito_residencia_${hijoNum}`, "");

		if (!deptoId) {
			setGeoData(prev => ({
				...prev,
				municipios: { ...prev.municipios, [hijoNum]: [] },
				distritos: { ...prev.distritos, [hijoNum]: [] }
			}));
			return;
		}

		const [munis, centro] = await Promise.all([
			Municipio(deptoId),
			PuntoCentral(deptoId)
		]);

		setGeoData(prev => ({
			...prev,
			municipios: { ...prev.municipios, [hijoNum]: munis || [] },
			distritos: { ...prev.distritos, [hijoNum]: [] },
			centros: { ...prev.centros, [hijoNum]: centro || { latitude: "13.79459900", longitude: "-88.93000000" } }
		}));
	};

	// Manejar cambios en municipio (residencia)
	const handleMunicipioChange = async (e, hijoNum) => {
		const muniId = e.target.value;
		setValue(`municipio_residencia_${hijoNum}`, muniId);
		setValue(`distrito_residencia_${hijoNum}`, "");

		if (!muniId) {
			setGeoData(prev => ({
				...prev,
				distritos: { ...prev.distritos, [hijoNum]: [] }
			}));
			return;
		}

		const dists = await Distrito(muniId);
		setGeoData(prev => ({
			...prev,
			distritos: { ...prev.distritos, [hijoNum]: dists || [] }
		}));
	};

	// Manejar cambios en departamento (cuidado)
	const handleDepartamentoCuidoChange = async (e, hijoNum) => {
		const deptoId = e.target.value;
		setValue(`departamento_cuido_${hijoNum}`, deptoId);
		setValue(`municipio_cuido_${hijoNum}`, "");
		setValue(`distrito_cuido_${hijoNum}`, "");

		if (!deptoId) {
			setGeoData(prev => ({
				...prev,
				municipios2: { ...prev.municipios2, [hijoNum]: [] },
				distritos2: { ...prev.distritos2, [hijoNum]: [] }
			}));
			return;
		}

		const [munis2, centro2] = await Promise.all([
			Municipio(deptoId),
			PuntoCentral(deptoId)
		]);

		setGeoData(prev => ({
			...prev,
			municipios2: { ...prev.municipios2, [hijoNum]: munis2 || [] },
			distritos2: { ...prev.distritos2, [hijoNum]: [] },
			centros2: { ...prev.centros2, [hijoNum]: centro2 || { latitude: "13.79459900", longitude: "-88.93000000" } }
		}));
	};

	// Manejar cambios en municipio (cuidado)
	const handleMunicipioCuidoChange = async (e, hijoNum) => {
		const muniId = e.target.value;
		setValue(`municipio_cuido_${hijoNum}`, muniId);
		setValue(`distrito_cuido_${hijoNum}`, "");

		if (!muniId) {
			setGeoData(prev => ({
				...prev,
				distritos2: { ...prev.distritos2, [hijoNum]: [] }
			}));
			return;
		}

		const dists2 = await Distrito(muniId);
		setGeoData(prev => ({
			...prev,
			distritos2: { ...prev.distritos2, [hijoNum]: dists2 || [] }
		}));
	};

	// Componente para seleccionar ubicación en mapa
	const LocationPicker = ({ hijoNum, tipo }) => {
		const map = useMap();
		const position = tipo === 'residencia'
			? geoData.positions[hijoNum]
			: geoData.positions2[hijoNum];
		const centro = tipo === 'residencia'
			? geoData.centros[hijoNum]
			: geoData.centros2[hijoNum];

		useEffect(() => {
			if (centro) {
				map.flyTo([parseFloat(centro.latitude), parseFloat(centro.longitude)], 12);
			}
		}, [centro, map]);

		useMapEvents({
			click(e) {
				const newPosition = e.latlng;
				if (tipo === 'residencia') {
					setGeoData(prev => ({
						...prev,
						positions: { ...prev.positions, [hijoNum]: newPosition }
					}));
					setValue(`latitud_residencia_${hijoNum}`, newPosition.lat);
					setValue(`longitud_residencia_${hijoNum}`, newPosition.lng);
				} else {
					setGeoData(prev => ({
						...prev,
						positions2: { ...prev.positions2, [hijoNum]: newPosition }
					}));
					setValue(`latitud_cuido_${hijoNum}`, newPosition.lat);
					setValue(`longitud_cuido_${hijoNum}`, newPosition.lng);
				}
			},
		});

		return position ? (
			<Marker position={[position.lat, position.lng]} icon={customIcon} />
		) : null;
	};

	// Función para enviar datos
	const onSubmit = async (data) => {
		try {
			let form = JSON.parse(sessionStorage.getItem("Empleado-Info")) || {};

			form.datos_colaborador = {
				...form.datos_colaborador,
				hijos_cant_conyugue: data.hijos_cant_conyugue,
				hijos_apli_conyugue: data.hijos_apli_conyugue
			};

			form.hijos_asociados = formatHijosData(data);
			form.hijos_declarados = formatHijosData(data);

			sessionStorage.setItem("Empleado-Info", JSON.stringify(form));
			navigate("/DefinicionServicio");
		} catch (error) {
			console.error("Error al enviar datos:", error);
		}
	};

	// Generar arrays para renderizar
	const renderHijosTotal = Array.from({ length: hijosTotal }, (_, i) => i + 1);
	const renderHijosAplican = Array.from({ length: hijosAplican }, (_, i) => i + 1);

	return (
		<div className="w-auto mr-6 font-bold p-10">
			<h1 className="text-3xl md:text-4xl font-bold text-[#0D7F9F] w-full mb-3">Datos de tus hijos</h1>
			<p className="font-normal text-sm md:text-base mb-5">
				En esta sección debes proporcionar la información de la cantidad total de hijos que tienes, no importando si están dentro de la edad de cobertura del beneficio CAPI. Completa todos los campos requeridos.
			</p>

			<form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col w-full ${totalErrors > 0 ? "form-errors" : ""}`} id="formulario">
				{/* Sección: Cantidad total de hijos */}
				<div className="flex flex-row justify-between w-auto p-4 border-2 rounded-lg items-center bg-[#D1ECF2] ml-2 mr-4 mb-4">
					<label htmlFor="hijos_cant_conyugue" className="font-semibold text-lg">¿Cuántos hijos tienes?</label>
					<input
						id="hijos_cant_conyugue"
						className={`w-20 border-2 p-4 text-lg font-normal rounded-lg outline-none ${errors.hijos_cant_conyugue ? "border-red-500" : ""}`}
						type="number"
						min="0"
						{...register('hijos_cant_conyugue', { 
							required: true, 
							min: {
								value: 1,
								message: "La cantidad de hijos debe ser mayor o igual a 1"
							}
						})}
						onChange={handleHijosTotalChange}
					/>
				</div>
				{errors.hijos_cant_conyugue && (
					<span className="text-red-500 text-end">{errors.hijos_cant_conyugue.message}</span>
				)}

				{/* Sección: Datos básicos de cada hijo */}
				{renderHijosTotal.map((num) => (
					<div key={`total-${num}`} className="text-center mb-6">
						<h1 className="text-3xl md:text-4xl mt-3 md:mt-6 font-bold text-[#0D7F9F] w-full mb-1">Hijo(a) {num}</h1>
						<p className='font-normal text-sm md:text-base mb-2'>
							Ingresa la fecha de nacimiento y el sexo de tu hijo(a) número {num}.
						</p>
						<div className="flex flex-col md:flex-row justify-center px-2">
							<div className="w-auto md:mr-2 md:w-1/3 mb-2">
								<label>Fecha de Nacimiento</label>
								<input
									className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`nacimiento_declarado_${num}`] ? 'border-red-600' : ''}`}
									type="date"
									{...register(`nacimiento_declarado_${num}`, {
										required: `La fecha de nacimiento de tu hijo número ${num} es requerida`
									})}
								/>
								{errors[`nacimiento_declarado_${num}`] && (
									<span className='text-red-600'>{errors[`nacimiento_declarado_${num}`].message}</span>
								)}
							</div>
							<div className="w-auto md:mx-2 md:w-1/3 mb-2">
								<label className="p-1">Sexo</label>
								<select
									className={`w-full border-2 p-4 h-2/3 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`sexo_declarado_${num}`] ? 'border-red-600' : ''}`}
									{...register(`sexo_declarado_${num}`, {
										required: `Selecciona el sexo de tu hijo número ${num}`
									})}
								>
									<option value="">Selecciona el sexo</option>
									<option value="masculino">Masculino</option>
									<option value="femenino">Femenino</option>
								</select>
								{errors[`sexo_declarado_${num}`] && (
									<span className='text-red-600'>{errors[`sexo_declarado_${num}`].message}</span>
								)}
							</div>
						</div>
					</div>
				))}

				{/* Sección: Hijos que aplican al beneficio */}
				<div className="flex flex-col justify-between w-auto p-4 border-2 rounded-lg items-center bg-[#D1ECF2] mt-3 ml-2 mr-4 mb-4">
					<div className='w-full'>
						<div onClick={() => setIsOpen(!isOpen)} className='cursor-pointer'>
							<div className='flex flex-row justify-between w-auto py-3'>
								<label htmlFor="hijos_apli_conyugue" className="font-semibold text-lg">
									¿Cuántos de tus hijos tienen cuatro años o menos?&nbsp;&nbsp;
									<span className='font-normal text-sm'>(Cliquea para más información)</span>
								</label>
								<select
									id="hijos_apli_conyugue"
									className="border-2 rounded-xl py-2 w-[150px] mr-3 bg-[#A9DDE9]"
									{...register("hijos_apli_conyugue", { required: true })}
									onChange={handleHijosAplicanChange}
									value={hijosAplican}
								>
									<option value="0">0</option>
									{Array.from({ length: hijosTotal }, (_, i) => (
										<option key={i + 1} value={i + 1}>{i + 1}</option>
									))}
								</select>
							</div>
							{isOpen && (
								<div className='rounded-xl border text-md p-5'>
									<p className='font-normal'>
										La cobertura de los CAPI que atienden a las hijas e hijos de las personas trabajadoras abarca a niñas y niños desde que finaliza la licencia por maternidad hasta un día antes de cumplir los cuatro años de edad. Si en el transcurso del año, el hijo o hija de las personas trabajadoras que asiste al centro cumple los cuatro años el beneficio no será interrumpido hasta finalizar el año lectivo. Puede revisar el artículo 138 de la Ley Crecer Juntos.
									</p>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Sección: Datos detallados de hijos que aplican */}
				{renderHijosAplican.map((num) => (
					<div key={`aplica-${num}`} className="mb-8">
						<h1 className="text-2xl md:text-3xl mt-3 md:mt-6 font-bold text-[#0D7F9F] w-full mb-1">
							Hijo(a) {num}: Información de los hijos en edad del rango de cobertura del beneficio CAPI
						</h1>	
						<p className='font-normal text-sm md:text-base mb-2'>
							En esta sección debes proporcionar la información correspondiente a los hijos entre 0-4 años de edad que entran en el rango de cobertura del beneficio CAPI. 
						</p>

						{/* Datos básicos */}
						<div className="flex flex-wrap md:flex-row px-2">
							<div className="w-auto md:w-1/4 mb-2 p-2">
								<label>Fecha de Nacimiento</label>
								<input
									className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`nacimiento_${num}`] ? 'border-red-600' : ''}`}
									type="date"
									{...register(`nacimiento_${num}`, {
										required: `La fecha de nacimiento de tu hijo requerida`,
										validate: value => {
											const nacimiento = new Date(value);
											const fechaActual = new Date();
											const edad = fechaActual.getFullYear() - nacimiento.getFullYear();
											return edad <= 4 || `La edad de tu hijo debe ser menor o igual a 4 años`;
										}
									})}
								/>
								{errors[`nacimiento_${num}`] && (
									<span className='text-red-600'>{errors[`nacimiento_${num}`].message}</span>
								)}
							</div>
							<div className="w-auto md:w-1/4 mb-2 p-2">
								<label className="p-1">Sexo</label>
								<select
									className={`w-full border-2 p-4 h-2/3 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`sexo_hijo_${num}`] ? 'border-red-600' : ''}`}
									{...register(`sexo_hijo_${num}`, {
										required: `Selecciona el sexo de tu hijo`
									})}
								>
									<option value="">Selecciona el sexo</option>
									<option value="masculino">Masculino</option>
									<option value="femenino">Femenino</option>
								</select>
								{errors[`sexo_hijo_${num}`] && (
									<span className='text-red-600'>{errors[`sexo_hijo_${num}`].message}</span>
								)}
							</div>
							<div className="w-auto md:w-2/4 mb-2 p-2">
								<label className="p-1">
									Nombre de la madre/padre del menor:
								</label>
								<input
									placeholder="Ingresa el nombre de la madre/padre"
									className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`nombre_padre-madre_${num}`] ? 'border-red-600' : ''}`}
									type="text"
									{...register(`nombre_padre-madre_${num}`, {
										required: `Debes ingresar la dirección donde reside tu hijo`
									})}
								/>
								{errors[`nombre_padre-madre_${num}`] && (
									<span className='text-red-600'>{errors[`nombre_padre-madre_${num}`].message}</span>
								)}
							</div>
							<div className="w-full mb-2">
								<label className="p-1">Dirección de residencia:</label>
								<input
									placeholder="Ingresa tu dirección"
									className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`direccion_residencia_${num}`] ? 'border-red-600' : ''}`}
									type="text"
									{...register(`direccion_residencia_${num}`, {
										required: `Debes ingresar la dirección donde reside tu hijo`
									})}
								/>
								{errors[`direccion_residencia_${num}`] && (
									<span className='text-red-600'>{errors[`direccion_residencia_${num}`].message}</span>
								)}
							</div>
						</div>

						{/* Ubicación de residencia */}
						<div className='flex flex-col md:flex-row px-2'>
							<div className='w-auto md:w-1/2 mb-2 md:mr-2'>
								<div className="w-auto mb-2">
									<label className="p-1">Departamento</label>
									<select
										className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`departamento_residencia_${num}`] ? 'border-red-600' : ''}`}
										{...register(`departamento_residencia_${num}`, {
											required: `Elige el departamento donde reside tu hijo`
										})}
										onChange={(e) => handleDepartamentoChange(e, num)}
									>
										<option value="">Seleccione su departamento</option>
										{departamento.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`departamento_residencia_${num}`] && (
										<span className='text-red-600'>{errors[`departamento_residencia_${num}`].message}</span>
									)}
								</div>
								<div className="w-auto mb-2">
									<label className="p-1">Municipio</label>
									<select
										className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`municipio_residencia_${num}`] ? 'border-red-600' : ''}`}
										{...register(`municipio_residencia_${num}`, {
											required: `Seleccione municipio para hijo ${num}`
										})}
										onChange={(e) => handleMunicipioChange(e, num)}
										disabled={!watch(`departamento_residencia_${num}`)}
									>
										<option value="">Seleccione municipio</option>
										{geoData.municipios[num]?.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`municipio_residencia_${num}`] && (
										<span className='text-red-600'>{errors[`municipio_residencia_${num}`].message}</span>
									)}
								</div>
								<div className="w-auto mb-2">
									<label className="p-1">Distrito</label>
									<select
										className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`distrito_residencia_${num}`] ? 'border-red-600' : ''}`}
										{...register(`distrito_residencia_${num}`, {
											required: `Seleccione distrito para hijo ${num}`
										})}
										disabled={!watch(`municipio_residencia_${num}`)}
									>
										<option value="">Seleccione distrito</option>
										{geoData.distritos[num]?.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`distrito_residencia_${num}`] && (
										<span className='text-red-600'>{errors[`distrito_residencia_${num}`].message}</span>
									)}
								</div>
							</div>
							<div className='w-auto md:w-1/2 md:mx-2 mb-2'>
								<label htmlFor='mapa'>Selecciona la ubicación del domicilio de tu hijo número {num}:</label>
								<MapContainer
									center={
										geoData.positions[num]
											? [geoData.positions[num].lat, geoData.positions[num].lng]
											: geoData.centros[num]
												? [parseFloat(geoData.centros[num].latitude), parseFloat(geoData.centros[num].longitude)]
												: [13.6989, -89.1914]
									}
									zoom={geoData.positions[num] ? 12 : 7}
									style={{ height: "300px", width: "100%" }}
								>
									<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
									<LocationPicker hijoNum={num} tipo="residencia" />
								</MapContainer>
								{geoData.positions[num] ? (
									<div className='flex flex-row justify-between'>
										<p>Latitud: {geoData.positions[num].lat.toFixed(6)}</p>
										<p>Longitud: {geoData.positions[num].lng.toFixed(6)}</p>
									</div>
								) : (
									<p className='text-gray-500'>No se ha seleccionado ubicación</p>
								)}
							</div>
						</div>

						{/* Información del encargado */}
						<h1 className="text-2xl md:text-3xl mt-3 md:mt-6 font-bold text-[#0D7F9F] w-full mb-1">
							Hijo(a) {num}: Información del encargado del cuidado
						</h1>
						<p className='font-normal text-sm md:text-base mb-2'>
							Proporciona la información sobre la persona encargada del cuidado diario de tu hijo(a) durante tu jornada de trabajo, asegurándote que la información sea precisa y esté actualizada.
						</p>

						<div className="flex flex-col md:flex-row px-2 items-end">
							<div className="w-auto md:mr-2 md:w-1/2 mb-2">
								<label>
									Actualmente, ¿Quién se encarga del cuidado de tu hijo(a) durante tu jornada de trabajo?
								</label>
								<select
									className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`persona_encargada_${num}`] ? 'border-red-600' : ''}`}
									{...register(`persona_encargada_${num}`, {
										required: `Elige a la persona encargada del cuidado de tu hijo`
									})}
								>
									<option value="">Encargado</option>
									{/* <option value="padre-madre">Padre/Madre</option> */}
									<option value="padre">Padre</option>
									<option value="madre">Madre</option>
									<option value="abuelos">Abuelos</option>
									<option value="otrosFamiliares">Otros familiares</option>
									<option value="empleadaDomestica">Empleada Doméstica</option>
									<option value="guarderia">Guardería</option>
								</select>
								{errors[`persona_encargada_${num}`] && (
									<span className='text-red-600'>{errors[`persona_encargada_${num}`].message}</span>
								)}
							</div>
							<div className="w-full md:mx-2 md:w-1/2 mb-2">
								<label className="p-1">Dirección donde cuidan al menor:</label>
								<input
									placeholder="Ingresa tu dirección"
									className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`direccion_cuido_${num}`] ? 'border-red-600' : ''}`}
									type="text"
									{...register(`direccion_cuido_${num}`, {
										required: `Escribe la dirección donde cuidan a tu hijo`
									})}
								/>
								{errors[`direccion_cuido_${num}`] && (
									<span className='text-red-600'>{errors[`direccion_cuido_${num}`].message}</span>
								)}
							</div>
						</div>

						{/* Ubicación de cuidado */}
						<div className='flex flex-col md:flex-row px-2'>
							<div className='w-auto md:w-1/2 md:mr-2 mb-2'>
								<div className="w-auto mb-2">
									<label className="p-1">Departamento</label>
									<select
										className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`departamento_cuido_${num}`] ? 'border-red-600' : ''}`}
										{...register(`departamento_cuido_${num}`, {
											required: `Seleccione departamento de cuidado para hijo ${num}`
										})}
										onChange={(e) => handleDepartamentoCuidoChange(e, num)}
									>
										<option value="">Seleccione departamento</option>
										{departamento.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`departamento_cuido_${num}`] && (
										<span className='text-red-600'>{errors[`departamento_cuido_${num}`].message}</span>
									)}
								</div>
								<div className="w-auto mb-2">
									<label className="p-1">Municipio</label>
									<select
										className={`w-full border-2 p-4 text-lg h-2/3 font-normal bg-[#D1ECF2] rounded-lg ${errors[`municipio_cuido_${num}`] ? 'border-red-600' : ''}`}
										{...register(`municipio_cuido_${num}`, {
											required: `Seleccione municipio de cuidado para hijo ${num}`
										})}
										onChange={(e) => handleMunicipioCuidoChange(e, num)}
										disabled={!watch(`departamento_cuido_${num}`)}
									>
										<option value="">Seleccione municipio</option>
										{geoData.municipios2[num]?.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`municipio_cuido_${num}`] && (
										<span className='text-red-600'>{errors[`municipio_cuido_${num}`].message}</span>
									)}
								</div>
								<div className="w-auto mb-2">
									<label className="p-1">Distrito</label>
									<select
										className={`w-full border-2 p-4 text-lg font-normal bg-[#D1ECF2] rounded-lg ${errors[`distrito_cuido_${num}`] ? 'border-red-600' : ''}`}
										{...register(`distrito_cuido_${num}`, {
											required: `Seleccione distrito de cuidado para hijo ${num}`
										})}
										disabled={!watch(`municipio_cuido_${num}`)}
									>
										<option value="">Seleccione distrito</option>
										{geoData.distritos2[num]?.map((item) => (
											<option key={item.id} value={item.id}>{item.name}</option>
										))}
									</select>
									{errors[`distrito_cuido_${num}`] && (
										<span className='text-red-600'>{errors[`distrito_cuido_${num}`].message}</span>
									)}
								</div>
							</div>
							<div className='w-auto md:w-1/2 md:mx-2 mb-2'>
								<label htmlFor='mapa'>Selecciona la ubicación donde cuidan a tu hijo número {num}:</label>
								<MapContainer
									center={
										geoData.positions2[num]
											? [geoData.positions2[num].lat, geoData.positions2[num].lng]
											: geoData.centros2[num]
												? [parseFloat(geoData.centros2[num].latitude), parseFloat(geoData.centros2[num].longitude)]
												: [13.6989, -89.1914]
									}
									zoom={geoData.positions2[num] ? 12 : 7}
									style={{ height: "300px", width: "100%" }}
								>
									<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
									<LocationPicker hijoNum={num} tipo="cuido" />
								</MapContainer>
								{geoData.positions2[num] ? (
									<div className='flex flex-row justify-between'>
										<p>Latitud: {geoData.positions2[num].lat.toFixed(6)}</p>
										<p>Longitud: {geoData.positions2[num].lng.toFixed(6)}</p>
									</div>
								) : (
									<p className='text-gray-500'>No se ha seleccionado ubicación</p>
								)}
							</div>
						</div>
					</div>
				))}

				{/* Botones de navegación */}
				<div className='flex justify-end mr-4'>
					<div className='w-[180px] mt-4 justify-between flex flex-row'>
						<Link to="/DatosTrabajador" data-discover="true">
							<button className="bg-white border-4 border-[#0E7FA0] font-medium drop-shadow-2xl shadow-2xl text-white rounded-full w-[50px] h-[50px] p-[12px]">
								<FontAwesomeIcon icon={faArrowLeft} className='text-[#0E7FA0] text-lg' />
							</button>
						</Link>
						<button
							className="bg-[#99C33E] font-medium drop-shadow-2xl shadow-2xl text-white rounded-full w-[125px] h-[50px] p-[12px]"
							type="submit"
						>
							Continuar
						</button>
					</div>
				</div>
			</form>
		</div>
	);
}

export default FormularioHijos;